import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PrivacyBadge from "../components/PrivacyBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Archive, Download, Eye, Lock, Send, Pencil } from "lucide-react";
import AppealDraftView from "../components/AppealDraftView";

const CHECK_STYLES = {
    complete: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Complete" },
    needs_confirmation: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", label: "Confirm" },
    recommended: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Recommended" },
    missing: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Missing" },
};

function EvidenceStatusPill({ status }) {
    const style = CHECK_STYLES[status] || CHECK_STYLES.recommended;
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded"
            style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
            data-testid={`case-evidence-status-${status || "recommended"}`}
        >
            {style.label}
        </span>
    );
}

export default function CaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [c, setC] = useState(null);
    const [docs, setDocs] = useState([]);
    const [events, setEvents] = useState([]);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
    const [pendingEdit, setPendingEdit] = useState(null);
    const [docVisibilityEdits, setDocVisibilityEdits] = useState({});
    const [shareReason, setShareReason] = useState("");
    const [approvalReason, setApprovalReason] = useState("");
    const [approvalResponse, setApprovalResponse] = useState("");
    const [editForm, setEditForm] = useState({ title: "", description: "", visibility: "private" });
    const [reportForm, setReportForm] = useState({ channel: "manual", reported_reference: "", reported_notes: "" });
    const [eventForm, setEventForm] = useState({ body: "", event_type: "comment", visibility: "", document_ids: [] });
    const [checklistNotes, setChecklistNotes] = useState({});
    const [privateDocsLoaded, setPrivateDocsLoaded] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            let caseRes;
            let adminAccessReason = "";
            try {
                caseRes = await api.get(`/cases/${id}`);
            } catch (err) {
                if (user?.role === "estate_admin" && err?.response?.status === 400) {
                    const reason = window.prompt("Reason for accessing this private resident case:");
                    if (!reason) throw err;
                    adminAccessReason = reason;
                    caseRes = await api.get(`/cases/${id}`, { params: { access_reason: reason } });
                } else {
                    throw err;
                }
            }
            const docParams = { case_id: id };
            if (adminAccessReason) {
                docParams.include_private_admin = true;
                docParams.access_reason = adminAccessReason;
                setPrivateDocsLoaded(true);
            } else {
                setPrivateDocsLoaded(false);
            }
            const [docsRes, eventsRes] = await Promise.all([
                api.get(`/documents`, { params: docParams }),
                api.get(`/cases/${id}/events`),
            ]);
            setC(caseRes.data);
            setStatus(caseRes.data.status);
            setDocs(docsRes.data || []);
            setDocVisibilityEdits(Object.fromEntries((docsRes.data || []).map((d) => [d.id, d.visibility])));
            setEditForm({
                title: caseRes.data.title,
                description: caseRes.data.description,
                visibility: caseRes.data.visibility,
            });
            setEvents(eventsRes.data || []);
            // For parking cases, also fetch the linked AI-drafted appeal (letter, grounds, citations).
            if (caseRes.data.category === "parking") {
                try {
                    const draftRes = await api.get(`/cases/${id}/appeal-draft`);
                    setDraft(draftRes.data || null);
                } catch {
                    setDraft(null);
                }
            } else {
                setDraft(null);
            }
        } catch {
            toast.error("Could not load case");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const fetchLinkedDocument = async (doc, accessReason = "") => {
        const params = accessReason ? { access_reason: accessReason } : {};
        return api.get(`/documents/${doc.id}/download`, { responseType: "blob", params });
    };

    const withLinkedDocumentAccess = async (doc, action) => {
        try {
            try {
                return await action(await fetchLinkedDocument(doc));
            } catch (err) {
                if (user?.role === "estate_admin" && err?.response?.status === 400) {
                    const reason = window.prompt("Reason for accessing this private resident document:");
                    if (!reason) throw err;
                    return await action(await fetchLinkedDocument(doc, reason));
                }
                throw err;
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open linked evidence");
            return null;
        }
    };

    const previewLinkedDocument = async (doc) => {
        await withLinkedDocumentAccess(doc, async (res) => {
            const contentType = res.headers?.["content-type"] || "application/octet-stream";
            const url = URL.createObjectURL(new Blob([res.data], { type: contentType }));
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        });
    };

    const downloadLinkedDocument = async (doc) => {
        await withLinkedDocumentAccess(doc, async (res) => {
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.original_filename || doc.title || "linked-evidence";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        });
    };

    const updateStatus = async (newStatus) => {
        try {
            await api.patch(`/cases/${id}`, { status: newStatus });
            setStatus(newStatus);
            toast.success("Status updated");
            load();
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error("Case is reported — status is locked");
            } else {
                toast.error("Failed to update status");
            }
        }
    };

    const needsSharingConsent = (fromVisibility, toVisibility) => {
        const wider = ["estate_shared", "agency_shared"];
        return fromVisibility !== toVisibility && wider.includes(toVisibility);
    };

    const submitEdit = async (payload) => {
        try {
            await api.patch(`/cases/${id}`, payload);
            toast.success("Case updated");
            setEditOpen(false);
            setShareConfirmOpen(false);
            setPendingEdit(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const confirmSharedEdit = async () => {
        if (!shareReason.trim()) {
            toast.error("Please add a reason for sharing");
            return;
        }
        try {
            await api.post(`/cases/${id}/share`, {
                visibility: pendingEdit.visibility,
                reason: shareReason,
                document_visibility: docVisibilityEdits,
            });
            const detailUpdates = {};
            if (pendingEdit.title !== c.title) detailUpdates.title = pendingEdit.title;
            if (pendingEdit.description !== c.description) detailUpdates.description = pendingEdit.description;
            if (Object.keys(detailUpdates).length > 0) {
                await api.patch(`/cases/${id}`, detailUpdates);
            }
            toast.success("Case sharing updated");
            setShareReason("");
            setShareConfirmOpen(false);
            setPendingEdit(null);
            load();
        } catch (err) {
            if (err?.response?.status === 409) {
                setApprovalReason(shareReason);
                toast.error("Household approval is required before sharing wider");
            } else {
                toast.error(err?.response?.data?.detail || "Could not update sharing choices");
            }
        }
    };

    const requestHouseholdApproval = async () => {
        const target = pendingEdit?.visibility || editForm.visibility;
        const reason = approvalReason.trim() || shareReason.trim();
        if (!reason) {
            toast.error("Add a reason for household approval");
            return;
        }
        try {
            await api.post(`/cases/${id}/household-share-approval`, {
                target_visibility: target,
                reason,
            });
            toast.success("Household approval requested");
            setApprovalReason("");
            setShareConfirmOpen(false);
            setPendingEdit(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not request household approval");
        }
    };

    const reviewHouseholdApproval = async (status) => {
        if (!approvalResponse.trim()) {
            toast.error("Add a response for the household approval review");
            return;
        }
        try {
            await api.post(`/cases/${id}/household-share-approval/review`, {
                status,
                response: approvalResponse,
            });
            toast.success(`Household approval ${status}`);
            setApprovalResponse("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not review household approval");
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (needsSharingConsent(c.visibility, editForm.visibility)) {
            setPendingEdit({ ...editForm });
            setEditOpen(false);
            setShareConfirmOpen(true);
            return;
        }
        submitEdit(editForm);
    };

    const submitReport = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/cases/${id}/report`, reportForm);
            toast.success("Case marked as reported and locked");
            setReportOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to report");
        }
    };

    const addEvent = async (e) => {
        e.preventDefault();
        if (!eventForm.body.trim()) return;
        try {
            await api.post(`/cases/${id}/events`, {
                body: eventForm.body,
                event_type: eventForm.event_type,
                visibility: eventForm.visibility || c.visibility,
                document_ids: eventForm.document_ids || [],
            });
            toast.success("Timeline updated");
            setEventForm({ body: "", event_type: "comment", visibility: "", document_ids: [] });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add timeline note");
        }
    };

    const unreport = async () => {
        if (!window.confirm("Admin override: unlock this reported case for further edits?")) return;
        try {
            await api.post(`/cases/${id}/unreport`);
            toast.success("Case unlocked");
            load();
        } catch {
            toast.error("Failed to unlock");
        }
    };

    const loadPrivateLinkedDocuments = async () => {
        const reason = window.prompt("Reason for loading private resident linked-document metadata:");
        if (!reason) return;
        try {
            const res = await api.get("/documents", {
                params: {
                    case_id: id,
                    include_private_admin: true,
                    access_reason: reason,
                },
            });
            setDocs(res.data || []);
            setDocVisibilityEdits(Object.fromEntries((res.data || []).map((d) => [d.id, d.visibility])));
            setPrivateDocsLoaded(true);
            toast.success("Private linked evidence metadata loaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load private linked evidence");
        }
    };

    const archiveCase = async () => {
        const confirmed = window.confirm(
            "Archive this case? It will disappear from active case lists and linked case-evidence uploads will be removed from normal access."
        );
        if (!confirmed) return;
        try {
            await api.delete(`/cases/${id}`);
            toast.success("Case archived");
            navigate("/cases");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not archive case");
        }
    };

    const updateChecklistItem = async (item, newStatus) => {
        try {
            await api.patch(`/cases/${id}/evidence-checklist/${item.id}`, {
                status: newStatus,
                note: checklistNotes[item.id] || undefined,
            });
            toast.success("Evidence checklist updated");
            setChecklistNotes((notes) => ({ ...notes, [item.id]: "" }));
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update evidence checklist");
        }
    };

    const copyAdminRequest = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Admin request copied");
        } catch {
            toast.info(text);
        }
    };

    const documentAllowedForEvent = (doc, visibility) => {
        if (doc.scope === "estate_kb") return true;
        if (visibility === "private" || visibility === "admin_only") return true;
        if (visibility === "household") return ["household", "estate_shared"].includes(doc.visibility);
        if (visibility === "estate_shared") return doc.visibility === "estate_shared";
        if (visibility === "agency_shared") return doc.visibility === "agency_shared";
        return false;
    };

    const eventVisibilityOptions = (caseVisibility) => {
        const options = [{ value: "private", label: "Private" }];
        if (["household", "estate_shared"].includes(caseVisibility)) {
            options.push({ value: "household", label: "Household" });
        }
        if (caseVisibility === "estate_shared") {
            options.push({ value: "estate_shared", label: "Estate-shared" });
        }
        if (caseVisibility === "agency_shared") {
            options.push({ value: "agency_shared", label: "Agency-shared" });
        }
        if (user?.role === "estate_admin") {
            options.push({ value: "admin_only", label: "Admin only" });
        }
        return options;
    };

    const shareDocumentVisibilityOptions = (doc) => {
        const targetVisibility = pendingEdit?.visibility;
        const options = [
            { value: "private", label: "Private" },
            { value: "household", label: "Household" },
        ];
        if (targetVisibility === "agency_shared") {
            options.push({ value: "agency_shared", label: "Agency-shared" });
        } else {
            options.push({ value: "estate_shared", label: "Estate-shared" });
        }
        if (doc.visibility === "estate_shared" && !options.some((option) => option.value === "estate_shared")) {
            options.push({ value: "estate_shared", label: "Estate-shared (not visible to agency)" });
        }
        if (doc.visibility === "agency_shared" && !options.some((option) => option.value === "agency_shared")) {
            options.push({ value: "agency_shared", label: "Agency-shared (not visible to residents)" });
        }
        if (doc.visibility === "admin_only" || user?.role === "estate_admin") {
            options.push({ value: "admin_only", label: "Admin only" });
        }
        return options;
    };

    const toggleEventDocument = (docId) => {
        setEventForm((current) => {
            const selected = current.document_ids || [];
            return {
                ...current,
                document_ids: selected.includes(docId)
                    ? selected.filter((id) => id !== docId)
                    : [...selected, docId],
            };
        });
    };

    const handleChecklistAction = (item) => {
        const target = item.action_target || {};
        if (item.action_type === "documents_upload") {
            const params = new URLSearchParams({
                upload: "1",
                scope: "case_evidence",
                case_id: id,
                title: target.title || item.label || "",
                description: target.description || item.reason || "",
            });
            navigate(`/documents?${params.toString()}`);
            return;
        }

        if (item.action_type === "admin_kb_request") {
            if (user?.role === "estate_admin") {
                const params = new URLSearchParams({
                    upload: "1",
                    scope: target.scope || "estate_kb",
                    kb_category: target.kb_category || "governing",
                    title: target.title || item.label || "",
                    description: target.request || item.reason || "",
                });
                navigate(`/documents?${params.toString()}`);
            } else {
                copyAdminRequest(target.request || item.action || "Please upload the missing estate knowledge-base document.");
            }
            return;
        }

        setEventForm((current) => ({
            ...current,
            body: current.body || `Evidence follow-up for ${item.label}: ${item.action || item.reason}`,
        }));
        toast.info("Added a suggested timeline note below");
    };

    if (loading) return <div className="text-sm text-slate-500" data-testid="case-detail-loading">Loading…</div>;
    if (!c) return <div className="text-sm text-slate-500">Case not found.</div>;

    const isOwner = c.owner_id === user?.id;
    const canEdit = (isOwner || user?.role === "estate_admin") && !c.is_reported;
    const canArchive = canEdit;
    const canUpdateChecklist = canEdit && c.category === "parking";
    const approval = c.household_share_approval;
    const canReviewHouseholdApproval = approval?.status === "requested" && approval?.requested_by !== user?.id && (
        user?.role === "estate_admin" || (user?.role === "household_admin" && user?.household_id === c.household_id)
    );
    const evidenceChecklist = c.evidence_checklist?.length
        ? c.evidence_checklist
        : (draft?.analysis?.evidence_checklist || []);
    const outstandingEvidence = evidenceChecklist.filter((item) => item.status !== "complete").length;
    const confirmationNeeded = evidenceChecklist.filter((item) => item.status === "needs_confirmation").length;
    const eventVisibility = eventForm.visibility || c.visibility;

    return (
        <div className="space-y-6" data-testid="case-detail-page">
            <div>
                <Link to="/cases" className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-700" data-testid="back-to-cases">← All cases</Link>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900" data-testid="case-title">{c.title}</h1>
                    <PrivacyBadge visibility={c.visibility} />
                    {c.is_reported && (
                        <span
                            data-testid="case-reported-badge"
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded bg-slate-100 text-slate-700 border-slate-300"
                        >
                            <Lock size={11} /> Reported · {c.reported_channel}
                        </span>
                    )}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                    {c.category} • Created {new Date(c.created_at).toLocaleString()}
                    {c.is_reported && c.reported_at && (
                        <> • Reported {new Date(c.reported_at).toLocaleString()}{c.reported_reference ? ` · ref ${c.reported_reference}` : ""}</>
                    )}
                </div>
            </div>

            {c.is_reported && (
                <div className="border border-slate-300 bg-slate-50 rounded-lg p-4 text-sm text-slate-700 flex items-start gap-3" data-testid="case-locked-banner">
                    <Lock size={16} className="mt-0.5" />
                    <div>
                        <div className="font-semibold text-slate-900">This case is locked from further edits</div>
                        <div className="text-slate-600 mt-0.5">
                            It was reported {c.reported_channel === "online" ? "online" : "manually"} on{" "}
                            {c.reported_at ? new Date(c.reported_at).toLocaleString() : "—"}.
                            {c.reported_notes && <> Notes: {c.reported_notes}</>}
                        </div>
                    </div>
                </div>
            )}

            {approval && (
                <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="household-share-approval-section">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500">Household sharing approval</div>
                            <div className="text-sm text-slate-700 mt-1">
                                {approval.status} request to share as {approval.target_visibility?.replace("_", " ")}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{approval.reason}</p>
                            {approval.response && <p className="text-sm text-slate-500 mt-1">Response: {approval.response}</p>}
                        </div>
                        <span className="text-xs uppercase tracking-widest text-slate-500">{approval.status}</span>
                    </div>
                    {canReviewHouseholdApproval && (
                        <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
                            <Textarea
                                rows={3}
                                value={approvalResponse}
                                onChange={(e) => setApprovalResponse(e.target.value)}
                                placeholder="Record why the household owner/admin approves or rejects wider sharing."
                                data-testid="household-approval-response"
                            />
                            <div className="flex gap-2 flex-wrap">
                                <Button type="button" className="bg-slate-700 hover:bg-slate-800 text-white" onClick={() => reviewHouseholdApproval("approved")} data-testid="approve-household-share">
                                    Approve
                                </Button>
                                <Button type="button" variant="outline" onClick={() => reviewHouseholdApproval("rejected")} data-testid="reject-household-share">
                                    Reject
                                </Button>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <section className="border border-slate-200 rounded-lg bg-white p-5">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Description</div>
                    {canEdit && (
                        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-case-button">
                            <Pencil size={12} className="mr-1.5" /> Edit
                        </Button>
                    )}
                </div>
                <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap" data-testid="case-description">{c.description}</p>
            </section>

            {c.category === "parking" && evidenceChecklist.length > 0 && (
                <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="case-evidence-checklist">
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500">PCN evidence checklist</div>
                            <div className="text-sm text-slate-500 mt-1">
                                Saved from the appeal analysis so the resident, admins and future reviewers can see what was complete or still needed.
                            </div>
                        </div>
                        <div className="text-xs text-slate-500" data-testid="case-evidence-outstanding">
                            {outstandingEvidence} outstanding{confirmationNeeded ? ` · ${confirmationNeeded} need confirmation` : ""}
                        </div>
                    </div>
                    <div className="space-y-2">
                        {evidenceChecklist.map((item) => (
                            <div key={item.id} className="border border-slate-200 rounded-md p-3" data-testid={`case-evidence-check-${item.id}`}>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="font-heading text-sm font-semibold text-slate-900">{item.label}</div>
                                    <EvidenceStatusPill status={item.status} />
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{item.reason}</p>
                                {item.status !== "complete" && (
                                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="text-xs text-slate-700 font-medium">
                                            Next: {item.action}
                                        </div>
                                        {canUpdateChecklist && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleChecklistAction(item)}
                                                data-testid={`case-evidence-action-${item.id}`}
                                            >
                                                Do this
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {item.status_note && (
                                    <div className="text-xs text-slate-500 mt-1">
                                        Note: {item.status_note}
                                    </div>
                                )}
                                {item.status === "needs_confirmation" && item.candidate_document_title && (
                                    <div className="mt-2 border border-blue-200 bg-blue-50 rounded-md p-2 text-xs text-blue-800" data-testid={`case-evidence-confirmation-${item.id}`}>
                                        Possible match: {item.candidate_document_title}. Confirm if this upload actually satisfies the checklist item.
                                    </div>
                                )}
                                {canUpdateChecklist && (
                                    <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                                        <Input
                                            value={checklistNotes[item.id] || ""}
                                            onChange={(e) => setChecklistNotes((notes) => ({ ...notes, [item.id]: e.target.value }))}
                                            placeholder="Optional note, e.g. uploaded permit photo"
                                            data-testid={`case-evidence-note-${item.id}`}
                                        />
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={item.status === "complete" ? "default" : "outline"}
                                                onClick={() => updateChecklistItem(item, "complete")}
                                                data-testid={`case-evidence-mark-complete-${item.id}`}
                                            >
                                                {item.status === "needs_confirmation" ? "Confirm match" : "Mark complete"}
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={item.status === "recommended" ? "default" : "outline"}
                                                onClick={() => updateChecklistItem(item, "recommended")}
                                                data-testid={`case-evidence-mark-recommended-${item.id}`}
                                            >
                                                Still recommended
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={item.status === "missing" ? "default" : "outline"}
                                                onClick={() => updateChecklistItem(item, "missing")}
                                                data-testid={`case-evidence-mark-missing-${item.id}`}
                                            >
                                                {item.status === "needs_confirmation" ? "Reject match" : "Mark missing"}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="border border-slate-200 rounded-lg bg-white p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Status</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {isOwner && !c.is_reported && (
                            <Button onClick={() => setReportOpen(true)} className="bg-slate-700 hover:bg-slate-800 text-white" size="sm" data-testid="mark-reported-button">
                                <Send size={12} className="mr-1.5" /> Mark as reported
                            </Button>
                        )}
                        {canArchive && (
                            <Button onClick={archiveCase} variant="outline" size="sm" data-testid="archive-case-button">
                                <Archive size={12} className="mr-1.5" /> Archive
                            </Button>
                        )}
                        {c.is_reported && user?.role === "estate_admin" && (
                            <Button onClick={unreport} variant="outline" size="sm" data-testid="unreport-button">
                                Admin: unlock
                            </Button>
                        )}
                    </div>
                </div>
                {canEdit ? (
                    <Select value={status} onValueChange={updateStatus}>
                        <SelectTrigger className="w-56" data-testid="case-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-slate-700" data-testid="case-status-readonly">{status}</div>
                )}
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Linked documents</div>
                        {user?.role === "estate_admin" && c.owner_id !== user?.id && (
                            <div className="text-xs text-slate-500 mt-1">
                                Private resident evidence metadata is hidden unless loaded with an audited reason.
                            </div>
                        )}
                    </div>
                    {user?.role === "estate_admin" && c.owner_id !== user?.id && !privateDocsLoaded && (
                        <Button type="button" variant="outline" size="sm" onClick={loadPrivateLinkedDocuments} data-testid="load-private-linked-documents">
                            Load private linked evidence
                        </Button>
                    )}
                </div>
                {docs.length === 0 ? (
                    <div className="text-sm text-slate-500" data-testid="case-docs-empty">No documents linked yet.</div>
                ) : (
                    <ul className="space-y-1.5">
                        {docs.map((d) => (
                            <li key={d.id} className="text-sm text-slate-800 flex items-center justify-between" data-testid={`case-doc-${d.id}`}>
                                <span>{d.title} <span className="text-slate-500 text-xs">({d.original_filename})</span></span>
                                <PrivacyBadge visibility={d.visibility} />
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="case-timeline-section">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Timeline</div>
                        <div className="text-sm text-slate-500 mt-1">Updates, reports, documents, and management notes for this case.</div>
                    </div>
                </div>

                {events.length === 0 ? (
                    <div className="text-sm text-slate-500" data-testid="case-events-empty">No timeline entries yet.</div>
                ) : (
                    <ol className="space-y-3" data-testid="case-events-list">
                        {events.map((ev) => (
                            <li key={ev.id} className="border border-slate-200 rounded-md p-3" data-testid={`case-event-${ev.id}`}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="text-xs uppercase tracking-widest text-slate-500">
                                        {ev.event_type.replace("_", " ")}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <PrivacyBadge visibility={ev.visibility} />
                                        <span className="text-xs text-slate-500">{new Date(ev.created_at).toLocaleString()}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{ev.body}</p>
                                {(ev.metadata?.response_source || ev.metadata?.reference || ev.metadata?.next_chase_date || ev.metadata?.due_date || ev.metadata?.status) && (
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                        {ev.metadata?.response_source && <span>Source: {ev.metadata.response_source}</span>}
                                        {ev.metadata?.reference && <span>Ref: {ev.metadata.reference}</span>}
                                        {ev.metadata?.status && <span>Status: {ev.metadata.status.replace("_", " ")}</span>}
                                        {ev.metadata?.due_date && <span>Due: {ev.metadata.due_date}</span>}
                                        {ev.metadata?.next_chase_date && <span>Next chase: {ev.metadata.next_chase_date}</span>}
                                    </div>
                                )}
                                {(ev.metadata?.linked_documents || []).length > 0 && (
                                    <div className="mt-2 border border-slate-100 rounded-md bg-slate-50 p-2" data-testid={`case-event-linked-docs-${ev.id}`}>
                                        <div className="text-[11px] uppercase tracking-widest text-slate-500">Linked evidence</div>
                                        <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {ev.metadata.linked_documents.map((doc) => (
                                                <div key={doc.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700">
                                                    <div className="min-w-0">
                                                        <div className="truncate font-medium">{doc.title}</div>
                                                        <div className="truncate text-slate-400">
                                                            {(doc.original_filename || doc.scope || "evidence")} - {doc.visibility?.replace("_", " ")}
                                                        </div>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            title="Preview linked evidence"
                                                            onClick={() => previewLinkedDocument(doc)}
                                                            data-testid={`case-event-preview-doc-${ev.id}-${doc.id}`}
                                                        >
                                                            <Eye className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            title="Download linked evidence"
                                                            onClick={() => downloadLinkedDocument(doc)}
                                                            data-testid={`case-event-download-doc-${ev.id}-${doc.id}`}
                                                        >
                                                            <Download className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="text-xs text-slate-500 mt-2">
                                    {ev.actor_email} ({ev.actor_role.replace("_", " ")})
                                </div>
                            </li>
                        ))}
                    </ol>
                )}

                {user?.role !== "agency_viewer" && (
                    <form onSubmit={addEvent} className="border-t border-slate-200 mt-5 pt-5 space-y-3" data-testid="case-event-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Note type</Label>
                                <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="comment">Comment</SelectItem>
                                        {user?.role === "estate_admin" && <SelectItem value="agency_response">Agency response</SelectItem>}
                                        {user?.role === "estate_admin" && <SelectItem value="agency_chase">Agency chase</SelectItem>}
                                        {user?.role === "estate_admin" && <SelectItem value="escalation">Escalation</SelectItem>}
                                        {user?.role === "estate_admin" && <SelectItem value="management_response">Management response</SelectItem>}
                                        {user?.role === "estate_admin" && <SelectItem value="resolution">Resolution</SelectItem>}
                                        {user?.role === "estate_admin" && <SelectItem value="admin_note">Admin note</SelectItem>}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Visibility</Label>
                                <Select value={eventForm.visibility || c.visibility} onValueChange={(v) => setEventForm({ ...eventForm, visibility: v })}>
                                    <SelectTrigger data-testid="event-visibility-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {eventVisibilityOptions(c.visibility).map((option) => (
                                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Timeline note</Label>
                            <Textarea
                                rows={3}
                                value={eventForm.body}
                                onChange={(e) => setEventForm({ ...eventForm, body: e.target.value })}
                                placeholder="Add a follow-up, operator response, management note, or resident update."
                                data-testid="event-body-input"
                            />
                        </div>
                        {docs.length > 0 && (
                            <div className="space-y-2" data-testid="event-linked-documents">
                                <Label>Link evidence documents</Label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {docs.map((doc) => {
                                        const allowed = documentAllowedForEvent(doc, eventVisibility);
                                        return (
                                            <label key={doc.id} className={`border rounded-md p-2 text-sm flex items-start gap-2 ${allowed ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 text-slate-400"}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={(eventForm.document_ids || []).includes(doc.id)}
                                                    disabled={!allowed}
                                                    onChange={() => toggleEventDocument(doc.id)}
                                                    data-testid={`event-document-${doc.id}`}
                                                />
                                                <span>
                                                    <span className="font-medium">{doc.title}</span>
                                                    <span className="block text-xs text-slate-500">
                                                        {doc.visibility.replace("_", " ")}
                                                        {!allowed ? ` - cannot be linked to ${eventVisibility.replace("_", " ")} notes` : ""}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-event-button">
                            Add timeline note
                        </Button>
                    </form>
                )}
            </section>

            {c.category === "parking" && draft && (
                <AppealDraftView draft={draft} />
            )}

            {c.category === "parking" && !draft && (
                <section className="border border-dashed border-slate-300 rounded-lg bg-white p-5 text-sm text-slate-500" data-testid="case-no-draft">
                    No AI-drafted appeal linked to this case yet.
                </section>
            )}

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent data-testid="edit-case-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit case</DialogTitle>
                        <DialogDescription>Adjust the case details and visibility. Once you mark this case as reported, edits will be locked.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveEdit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Title</Label>
                            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required data-testid="edit-title-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Description</Label>
                            <Textarea rows={5} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} required data-testid="edit-description-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Visibility</Label>
                            <Select value={editForm.visibility} onValueChange={(v) => setEditForm({ ...editForm, visibility: v })}>
                                <SelectTrigger data-testid="edit-visibility-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="private">Private</SelectItem>
                                    <SelectItem value="household">Household</SelectItem>
                                    <SelectItem value="estate_shared">Estate-shared</SelectItem>
                                    <SelectItem value="agency_shared">Agency-shared</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-edit-button">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={shareConfirmOpen} onOpenChange={setShareConfirmOpen}>
                <DialogContent data-testid="share-consent-dialog">
                    <DialogHeader>
                        <DialogTitle>Confirm wider sharing</DialogTitle>
                        <DialogDescription>
                            You are changing this case from {c.visibility.replace("_", " ")} to {pendingEdit?.visibility?.replace("_", " ")}.
                            This can make the case title, description, timeline notes, linked documents, and AI outputs visible to a wider audience depending on each item's visibility.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-4 text-sm">
                        <div className="font-semibold">Check before sharing</div>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Remove personal details that do not need to be shared.</li>
                            <li>Do not share title deeds or private evidence unless necessary.</li>
                            <li>Agency-shared cases can be seen by estate agency viewers, but only documents marked agency-shared are shared with them.</li>
                        </ul>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Reason for sharing</Label>
                        <Textarea
                            rows={3}
                            value={shareReason}
                            onChange={(e) => setShareReason(e.target.value)}
                            placeholder="Example: Sharing with the agency so they can respond to repeated incorrect PCN enforcement."
                            data-testid="share-reason-input"
                        />
                    </div>
                    {c.visibility === "household" && user?.role === "resident" && (
                        <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-sm text-slate-700" data-testid="household-approval-hint">
                            Household-visible cases need household admin approval before they are shared wider. If confirmation fails, request approval here and ask your household admin to review this case.
                            <div className="mt-3">
                                <Button type="button" variant="outline" onClick={requestHouseholdApproval} data-testid="request-household-approval-button">
                                    Request household approval
                                </Button>
                            </div>
                        </div>
                    )}
                    {docs.length > 0 && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="share-document-review">
                            <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                Linked document visibility
                            </div>
                            <div className="divide-y divide-slate-100">
                                {docs.map((doc) => (
                                    <div key={doc.id} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3 items-center" data-testid={`share-doc-${doc.id}`}>
                                        <div>
                                            <div className="text-sm font-medium text-slate-900">{doc.title}</div>
                                            <div className="text-xs text-slate-500">{doc.original_filename}</div>
                                        </div>
                                        <Select
                                            value={docVisibilityEdits[doc.id] || doc.visibility}
                                            onValueChange={(v) => setDocVisibilityEdits({ ...docVisibilityEdits, [doc.id]: v })}
                                        >
                                            <SelectTrigger data-testid={`share-doc-visibility-${doc.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {shareDocumentVisibilityOptions(doc).map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {pendingEdit?.visibility === "agency_shared" && (docVisibilityEdits[doc.id] || doc.visibility) !== "agency_shared" && (
                                            <div className="md:col-start-2 text-xs text-slate-500">
                                                This document will not be visible to the agency.
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => {
                                setShareConfirmOpen(false);
                                setEditOpen(true);
                            }}
                            data-testid="cancel-share-consent-button"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            className="bg-slate-700 hover:bg-slate-800 text-white"
                            onClick={confirmSharedEdit}
                            data-testid="confirm-share-consent-button"
                        >
                            Confirm sharing
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Report dialog */}
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogContent data-testid="report-dialog">
                    <DialogHeader>
                        <DialogTitle>Mark case as reported</DialogTitle>
                        <DialogDescription>
                            Choose how you submitted the appeal. Once marked, this case will be locked from further edits to preserve an accurate audit trail. An admin can unlock if needed.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitReport} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Channel</Label>
                            <Select value={reportForm.channel} onValueChange={(v) => setReportForm({ ...reportForm, channel: v })}>
                                <SelectTrigger data-testid="report-channel-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="online">Online (operator portal)</SelectItem>
                                    <SelectItem value="manual">Manual (post / in person / email)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Operator reference (optional)</Label>
                            <Input value={reportForm.reported_reference} onChange={(e) => setReportForm({ ...reportForm, reported_reference: e.target.value })} placeholder="e.g. ticket number from the operator" data-testid="report-reference-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Notes (optional)</Label>
                            <Textarea rows={3} value={reportForm.reported_notes} onChange={(e) => setReportForm({ ...reportForm, reported_notes: e.target.value })} placeholder="When and how you submitted, any acknowledgements received…" data-testid="report-notes-input" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-report-button">Mark as reported & lock</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
